// SubscriptionGate Component
// Blocks or prompts users when they hit subscription limits

import React from 'react';
import { Box, Typography, Button, Alert, LinearProgress, Divider, Card, Chip, Stack, Modal, ModalDialog, ModalClose, CircularProgress } from '@mui/joy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../utils/supabaseClient';

interface SubscriptionGateProps {
  userId: string | null;
  feature: 'family' | 'predictions' | 'yogas' | 'full_analysis' | 'email_support' | 'reminders';
  featureLabel?: string;
  children: React.ReactNode;
  showUpgradePrompt?: boolean;
}

export function SubscriptionGate({
  userId,
  feature,
  featureLabel,
  children,
  showUpgradePrompt = true,
}: SubscriptionGateProps) {
  const { subscription, loading, canAccess, getUpgradeMessage } = useSubscription(userId);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography level="body-sm" sx={{ mt: 1 }}>
          Loading subscription...
        </Typography>
      </Box>
    );
  }

  if (!subscription) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Unable to load subscription details. Please refresh the page.
      </Alert>
    );
  }

  const featureKey = `canAccess${feature.charAt(0).toUpperCase() + feature.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}` as keyof typeof subscription.limits;
  const hasAccess = canAccess(featureKey);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  const label = featureLabel || feature.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Box
      sx={{
        p: 3,
        textAlign: 'center',
        borderRadius: 'md',
        bgcolor: 'background.level1',
        border: '1px dashed',
        borderColor: 'divider',
        m: 2,
      }}
    >
      <Typography level="h4" sx={{ mb: 1 }}>
        🔒 {label}
      </Typography>
      <Typography level="body-md" sx={{ mb: 2, color: 'text.secondary' }}>
        {getUpgradeMessage(label)}
      </Typography>

      {subscription.plan === 'free_trial' && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="solid" color="primary">
            Upgrade to Silver
          </Button>
          <Button variant="solid" color="success">
            Upgrade to Gold
          </Button>
        </Box>
      )}

      {subscription.plan === 'silver' && (
        <Button variant="solid" color="success">
          Upgrade to Gold
        </Button>
      )}

      {subscription.status === 'expired' && (
        <Button variant="solid" color="warning">
          Renew Subscription
        </Button>
      )}

      {subscription.status === 'pending_approval' && (
        <Typography level="body-sm" sx={{ mt: 2, fontStyle: 'italic' }}>
          Please wait for admin approval to access your trial.
        </Typography>
      )}
    </Box>
  );
}

// QuotaWarning Component - shows remaining quota
interface QuotaWarningProps {
  userId: string | null;
  type: 'charts' | 'chat_general' | 'chat_life_area';
  threshold?: number; // show warning when quota <= threshold
}

export function QuotaWarning({ userId, type, threshold = 1 }: QuotaWarningProps) {
  const { subscription, usage, getRemainingQuota } = useSubscription(userId);

  if (!subscription || !usage) return null;

  const remaining = getRemainingQuota(type);

  if (remaining > threshold) return null;

  const labels = {
    charts: 'birth profiles',
    chat_general: 'general questions this month',
    chat_life_area: 'life area explorations this month',
  };

  const colors = {
    0: 'danger' as const,
    1: 'warning' as const,
  };

  return (
    <Alert
      color={colors[remaining as 0 | 1] || 'warning'}
      variant="soft"
      sx={{ mb: 2 }}
    >
      <Typography level="body-sm">
        {remaining === 0 ? (
          <>
            ⚠️ You've reached your limit for {labels[type]}. 
            {subscription.plan === 'free_trial' && ' Upgrade to Silver or Gold for more access.'}
            {subscription.plan === 'silver' && type !== 'charts' && ' Upgrade to Gold for more questions.'}
          </>
        ) : (
          <>
            ⚠️ You have {remaining} {labels[type]} remaining.
          </>
        )}
      </Typography>
    </Alert>
  );
}

// TrialBanner Component - shows trial expiration countdown
interface TrialBannerProps {
  userId: string | null;
}

export function TrialBanner({ userId }: TrialBannerProps) {
  const { subscription } = useSubscription(userId);

  if (!subscription || subscription.plan !== 'free_trial') return null;
  if (subscription.status !== 'active') return null;
  if (!subscription.daysRemaining) return null;

  const daysLeft = subscription.daysRemaining;
  const color = daysLeft <= 2 ? 'danger' : daysLeft <= 4 ? 'warning' : 'primary';

  return (
    <Alert color={color} variant="soft" sx={{ mb: 2 }}>
      <Typography level="body-md" fontWeight="bold">
        {daysLeft <= 0 ? (
          <>⏰ Your trial has expired. Upgrade to continue accessing all features.</>
        ) : (
          <>⏰ {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left in your free trial</>
        )}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button size="sm" variant="solid" color={color}>
          Upgrade to Silver
        </Button>
        <Button size="sm" variant="solid" color="success">
          Upgrade to Gold
        </Button>
      </Box>
    </Alert>
  );
}

// ===== ExpiredTrialOverlay =====
// Full-screen hard block shown when subscription.status === 'expired'
interface ExpiredTrialOverlayProps {
  onSignOut: () => void;
  onUpgradeSuccess: () => void;
}

interface PlanOption {
  id: string;
  plan_name: 'silver' | 'gold';
  display_name: string;
  price_inr: number;
  features_json: string[];
}

export function ExpiredTrialOverlay({ onSignOut, onUpgradeSuccess }: ExpiredTrialOverlayProps) {
  const [plans, setPlans] = React.useState<PlanOption[]>([]);
  const [paying, setPaying] = React.useState<string | null>(null); // plan_id being paid
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    supabase
      .from('subscription_plans')
      .select('id, plan_name, display_name, price_inr, features_json')
      .eq('is_active', true)
      .eq('region', 'india')
      .eq('billing_cycle', 'yearly')
      .order('display_order')
      .then(({ data }) => {
        if (data) setPlans(data.filter((p: PlanOption) => p.plan_name !== 'trial' as any));
      });
  }, []);

  const handleUpgrade = async (plan: PlanOption) => {
    setError(null);
    setPaying(plan.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('User email not found');

      // Create Razorpay order via edge function (user is already authenticated)
      const orderRes = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-subscription-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          },
          body: JSON.stringify({
            plan_id: plan.id,
            user_email: user.email,
            user_details: { full_name: user.user_metadata?.full_name || '' },
          }),
        }
      );

      if (!orderRes.ok) {
        const err = await orderRes.json();
        throw new Error(err.error || 'Failed to create order');
      }

      const { order, subscription_id } = await orderRes.json();

      // Open Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'ShreemAstro',
        description: plan.display_name,
        order_id: order.id,
        prefill: { email: user.email },
        theme: { color: '#667eea' },
        handler: async (response: any) => {
          try {
            const verifyRes = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-payment`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`,
                  'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
                },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  subscription_id,
                }),
              }
            );
            if (!verifyRes.ok) throw new Error('Payment verification failed');
            // Clear subscription cache so useSubscription re-fetches
            const { data: { user: u } } = await supabase.auth.getUser();
            if (u?.id) localStorage.removeItem(`user_subscription_${u.id}_v1`);
            onUpgradeSuccess();
          } catch (e: any) {
            setError('Payment verified but activation failed. Please contact support.');
          } finally {
            setPaying(null);
          }
        },
        modal: {
          ondismiss: () => setPaying(null),
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
      setPaying(null);
    }
  };

  const handleSignOut = async () => {
    sessionStorage.removeItem('landingDismissed');
    sessionStorage.setItem('scrollToPricing', 'true');
    await supabase.auth.signOut();
    onSignOut();
  };

  const silverPlan = plans.find(p => p.plan_name === 'silver');
  const goldPlan = plans.find(p => p.plan_name === 'gold');

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 10, 30, 0.88)',
        backdropFilter: 'blur(6px)',
        p: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 480,
          width: '100%',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          bgcolor: '#fff',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #c2185b 100%)',
            px: 3,
            py: 2.5,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '2rem', mb: 0.25 }}>⏰</Typography>
          <Typography level="h3" sx={{ color: '#fff', fontWeight: 700 }}>
            Your Free Trial Has Expired
          </Typography>
          <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
            Upgrade now to continue — no sign-in required
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
            Your charts, predictions, and AI insights are waiting. Pick a plan to resume instantly.
          </Typography>

          {error && (
            <Alert color="danger" variant="soft" sx={{ mb: 2, fontSize: '0.8rem' }}>
              {error}
            </Alert>
          )}

          {/* Plan cards — same design as LandingPage pricing section */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: 'stretch' }}>

            {/* Silver */}
            {silverPlan && (
              <Card
                variant="outlined"
                sx={{
                  flex: 1, p: 2.5,
                  display: 'flex', flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  borderColor: 'rgba(192,192,192,0.3)',
                  '&:hover': {
                    boxShadow: '0 16px 48px rgba(192,192,192,0.2)',
                    transform: 'translateY(-4px)',
                    borderColor: '#C0C0C0',
                  },
                }}
              >
                <Typography level="h4" sx={{ mb: 0.5, color: '#C0C0C0', fontWeight: 700 }}>Silver</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>₹62/month · Perfect for families</Typography>
                <Typography level="h3" sx={{ mb: 1.5, fontWeight: 800 }}>
                  ₹{silverPlan.price_inr.toLocaleString()}
                  <Typography level="body-xs" component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>/year</Typography>
                </Typography>
                <Stack spacing={0.75} sx={{ flex: 1, mb: 2 }}>
                  {['Self & Spouse + 2 family members', 'Life Areas Dashboard & Remedies', '3 general AI chat questions/month', 'Yogas & predictions', 'Divisional charts', 'Future Event Predictor'].map(f => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#C0C0C0', mt: 0.1, flexShrink: 0 }} />
                      <Typography level="body-xs">{f}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  variant="outlined"
                  loading={paying === silverPlan.id}
                  disabled={!!paying}
                  onClick={() => handleUpgrade(silverPlan)}
                  sx={{
                    borderColor: '#C0C0C0', color: '#C0C0C0',
                    '&:hover': { background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)', color: '#fff', borderColor: 'transparent' },
                  }}
                >
                  Upgrade to Silver
                </Button>
              </Card>
            )}

            {/* Gold */}
            {goldPlan && (
              <Card
                variant="outlined"
                sx={{
                  flex: 1, p: 2.5,
                  display: 'flex', flexDirection: 'column',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  borderColor: 'rgba(218,165,32,0.5)',
                  borderWidth: 2,
                  '&:hover': {
                    boxShadow: '0 20px 60px rgba(218,165,32,0.3)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Chip
                  variant="solid"
                  size="sm"
                  sx={{
                    position: 'absolute', top: -10, right: 16,
                    background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                    color: '#fff', fontWeight: 700, fontSize: '10px',
                  }}
                >
                  ⭐ POPULAR
                </Chip>
                <Typography level="h4" sx={{ mb: 0.5, color: '#DAA520', fontWeight: 700 }}>Gold</Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>₹97/month · Premium features</Typography>
                <Typography level="h3" sx={{ mb: 1.5, fontWeight: 800 }}>
                  ₹{goldPlan.price_inr.toLocaleString()}
                  <Typography level="body-xs" component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>/year</Typography>
                </Typography>
                <Stack spacing={0.75} sx={{ flex: 1, mb: 2 }}>
                  {['All of Silver +', 'Self & Spouse + 4 family members', 'Marriage Compatibility Analysis', 'Personal Muhurat Advisor', 'Life Event Finder', '6 general AI chat questions/month'].map(f => (
                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#DAA520', mt: 0.1, flexShrink: 0 }} />
                      <Typography level="body-xs" sx={{ fontWeight: f === 'All of Silver +' ? 700 : 400 }}>{f}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  loading={paying === goldPlan.id}
                  disabled={!!paying}
                  onClick={() => handleUpgrade(goldPlan)}
                  sx={{
                    background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                    color: '#fff', border: 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)', boxShadow: '0 8px 24px rgba(184,134,11,0.3)' },
                  }}
                >
                  Upgrade to Gold
                </Button>
              </Card>
            )}
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Footer links */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              level="body-xs"
              onClick={handleSignOut}
              sx={{ color: 'text.tertiary', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'text.secondary' } }}
            >
              View all plans & sign out
            </Typography>
            <Typography
              level="body-xs"
              onClick={handleSignOut}
              sx={{ color: 'text.tertiary', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'text.secondary' } }}
            >
              Sign out
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ===== MySubscriptionModal =====
// Branded modal accessible from the Settings gear menu.
// Adapts to the user's current plan: Trial → shows upgrade cards;
// Silver/Gold → shows plan status, renewal info, cancel option; Expired → upgrade prompt.

export interface MySubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  onUpgradeSuccess: () => void;
  chatCreditsRemaining?: number | null;
  onBuyCredits?: () => void;
}

export function MySubscriptionModal({ open, onClose, userId, onUpgradeSuccess, chatCreditsRemaining, onBuyCredits }: MySubscriptionModalProps) {
  const { subscription: subInfo, usage: subUsage } = useSubscription(userId);
  const [plans, setPlans] = React.useState<PlanOption[]>([]);
  const [subRecord, setSubRecord] = React.useState<any>(null);
  const [paying, setPaying] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !userId) return;
    setError(null);
    Promise.all([
      supabase
        .from('subscription_plans')
        .select('id, plan_name, display_name, price_inr, features_json')
        .eq('is_active', true).eq('region', 'india').eq('billing_cycle', 'yearly')
        .order('display_order'),
      supabase
        .from('user_subscriptions')
        .select('*, subscription_plans(*)')
        .eq('user_id', userId)
        .not('status', 'eq', 'pending_payment')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]).then(([plansRes, subRes]) => {
      if (plansRes.data) setPlans(plansRes.data.filter((p: PlanOption) => (p.plan_name as string) !== 'trial'));
      if (subRes.data) setSubRecord(subRes.data);
    });
  }, [open, userId]);

  // Razorpay upgrade flow (same logic as ExpiredTrialOverlay)
  const handleUpgrade = async (plan: PlanOption) => {
    setError(null);
    setPaying(plan.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('User email not found');

      const orderRes = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-subscription-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          },
          body: JSON.stringify({
            plan_id: plan.id,
            user_email: user.email,
            user_details: { full_name: user.user_metadata?.full_name || '' },
          }),
        }
      );
      if (!orderRes.ok) {
        const err = await orderRes.json();
        throw new Error(err.error || 'Failed to create order');
      }
      const { order, subscription_id } = await orderRes.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'ShreemAstro',
        description: plan.display_name,
        order_id: order.id,
        prefill: { email: user.email },
        theme: { color: '#B8860B' },
        handler: async (response: any) => {
          try {
            const verifyRes = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-payment`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`,
                  'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
                },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  subscription_id,
                }),
              }
            );
            if (!verifyRes.ok) throw new Error('Payment verification failed');
            if (userId) localStorage.removeItem(`user_subscription_${userId}_v2`);
            onUpgradeSuccess();
            onClose();
          } catch (e: any) {
            setError('Payment verified but activation failed. Please contact support.');
          } finally {
            setPaying(null);
          }
        },
        modal: { ondismiss: () => setPaying(null) },
      };
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
      setPaying(null);
    }
  };

  const plan = subInfo?.plan ?? 'free_trial';
  const status = subInfo?.status ?? 'active';
  const daysLeft = subInfo?.daysRemaining ?? null;

  const isPaidPlan = plan === 'silver' || plan === 'gold';
  const isExpired = status === 'expired';
  const isAdmin = plan === 'admin';
  const showUpgradeCards = !isPaidPlan || isExpired;

  // Header styling adapts to plan
  const headerGradient = isExpired
    ? 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #c2185b 100%)'
    : plan === 'gold'
    ? 'linear-gradient(135deg, #8B6914 0%, #DAA520 50%, #FFD700 100%)'
    : plan === 'silver'
    ? 'linear-gradient(135deg, #607080 0%, #A0A8B0 50%, #C8D0D8 100%)'
    : 'linear-gradient(135deg, #4a1060 0%, #8B0030 50%, #c2185b 100%)';

  const planEmoji = isAdmin ? '⚙️' : plan === 'gold' ? '🥇' : plan === 'silver' ? '🥈' : isExpired ? '⏰' : '🎁';
  const planLabel = isAdmin ? 'Admin Access'
    : plan === 'gold' ? 'My Gold Plan'
    : plan === 'silver' ? 'My Silver Plan'
    : isExpired ? 'Trial Expired'
    : 'My Free Trial';

  const subEndDate = subRecord?.subscription_end_date
    ? new Date(subRecord.subscription_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const trialEndDate = subInfo?.trialEndDate
    ? new Date(subInfo.trialEndDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const silverPlan = plans.find(p => p.plan_name === 'silver');
  const goldPlan   = plans.find(p => p.plan_name === 'gold');

  // Trial progress: 7-day standard trial
  const trialTotalDays = 7;
  const trialUsed = subInfo?.trialEndDate
    ? Math.max(0, trialTotalDays - (daysLeft ?? 0))
    : trialTotalDays;
  const trialPct = Math.min(100, (trialUsed / trialTotalDays) * 100);

  // Subscription period % for paid plan
  const billingCycle = subRecord?.subscription_plans?.billing_cycle;
  const totalBillingDays = billingCycle === 'monthly' ? 30 : 365;
  const subUsedPct = isPaidPlan && daysLeft !== null
    ? Math.max(0, Math.min(100, ((totalBillingDays - daysLeft) / totalBillingDays) * 100))
    : 0;

  const canCancel = false; // Cancel flow removed — use Renew section instead
  const inTrial = plan === 'free_trial' && status === 'active';
  const expiringWarning = isPaidPlan && daysLeft !== null && daysLeft <= 30 && daysLeft > 0;

  // Plan feature list from DB, fall back to a sensible default
  const planFeatures: string[] = subRecord?.subscription_plans?.features_json ?? [];

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10010 }}>
      <ModalDialog
        variant="plain"
        sx={{
          p: 0,
          maxWidth: 520,
          width: '95vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
        }}
      >
        {/* Gradient header */}
        <Box sx={{ background: headerGradient, px: 3, py: 2.5, position: 'relative', flexShrink: 0 }}>
          <ModalClose sx={{ color: 'rgba(255,255,255,0.85)', top: 10, right: 10, '&:hover': { background: 'rgba(255,255,255,0.15)' } }} />
          <Typography sx={{ fontSize: '2rem', lineHeight: 1, mb: 0.5 }}>{planEmoji}</Typography>
          <Typography level="h3" sx={{ color: '#fff', fontWeight: 700, fontFamily: '"Cinzel", serif' }}>{planLabel}</Typography>
          {status === 'active' && daysLeft !== null && !isAdmin && (
            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
              {inTrial ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining in your trial`
                : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} until renewal`}
            </Typography>
          )}
          {isExpired && (
            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
              Upgrade now to resume access instantly
            </Typography>
          )}
          {isAdmin && (
            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
              Unlimited access — no subscription required
            </Typography>
          )}
        </Box>

        {/* Scrollable body */}
        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 110px)', px: 3, py: 2.5 }}>

          {/* Chat credits row — show for non-admin users */}
          {!isAdmin && (() => {
            const maxPerMonth = subInfo?.limits?.maxChatGeneral ?? 0;
            const yearlyMax = maxPerMonth * 12;
            const yearlyUsed = subUsage?.chatGeneralCount ?? 0;
            const yearlyRemaining = Math.max(0, yearlyMax - yearlyUsed);
            const bonusCredits = chatCreditsRemaining ?? 0;

            return (
              <Box sx={{ mb: 2.5, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: maxPerMonth > 0 && maxPerMonth < 999999 ? 1.5 : 0 }}>
                  <Typography level="title-sm">AI Chat Credits</Typography>
                  {onBuyCredits && maxPerMonth > 0 && maxPerMonth < 999999 && (
                    <Button size="sm" variant="soft" color="primary" onClick={() => onBuyCredits()} sx={{ fontWeight: 700 }}>
                      Buy Credits
                    </Button>
                  )}
                </Box>
                {maxPerMonth === 999999 ? (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Unlimited credits</Typography>
                ) : maxPerMonth === 0 ? (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Not included in your plan</Typography>
                ) : (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: bonusCredits > 0 ? 1 : 0 }}>
                      {([
                        { label: 'Per month', value: maxPerMonth, color: 'text.primary' },
                        { label: 'Per year', value: yearlyMax, color: 'text.primary' },
                        { label: 'Used this year', value: yearlyUsed, color: yearlyUsed > 0 ? 'warning.600' : 'text.primary' },
                        { label: 'Remaining', value: yearlyRemaining, color: yearlyRemaining === 0 ? 'danger.600' : 'success.600' },
                      ] as const).map(({ label, value, color }) => (
                        <Box key={label} sx={{ textAlign: 'center', p: 1, bgcolor: 'background.surface', borderRadius: 'sm' }}>
                          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
                          <Typography level="title-sm" sx={{ fontWeight: 800, color }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                    {bonusCredits > 0 && (
                      <Typography level="body-xs" sx={{ color: 'success.600', fontWeight: 600 }}>
                        +{bonusCredits} bonus purchased credits
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            );
          })()}

          {/* Success / error banners */}
          {error && (
            <Alert color="danger" variant="soft" sx={{ mb: 2, fontSize: '0.82rem' }}>{error}</Alert>
          )}

          {/* Admin — simple info card */}
          {isAdmin && (
            <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md', textAlign: 'center' }}>
              <WorkspacePremiumIcon sx={{ fontSize: 36, color: '#b83050', mb: 1 }} />
              <Typography level="body-md" sx={{ fontWeight: 600 }}>Full admin access</Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                All features unlocked — no billing required.
              </Typography>
            </Box>
          )}

          {/* Free trial — progress bar */}
          {inTrial && trialEndDate && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography level="title-sm">Trial Progress</Typography>
                <Typography level="body-sm" sx={{
                  fontWeight: 700,
                  color: (daysLeft ?? 0) <= 2 ? 'danger.600' : (daysLeft ?? 0) <= 4 ? 'warning.600' : 'success.600',
                }}>
                  {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                </Typography>
              </Box>
              <LinearProgress
                determinate
                value={trialPct}
                color={(daysLeft ?? 0) <= 2 ? 'danger' : (daysLeft ?? 0) <= 4 ? 'warning' : 'success'}
                sx={{ mb: 1, borderRadius: 'sm' }}
              />
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Trial ends {trialEndDate}
              </Typography>
            </Box>
          )}

          {/* Paid plan — subscription period */}
          {isPaidPlan && !isExpired && subEndDate && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography level="title-sm">Subscription Period</Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color={expiringWarning ? 'warning' : 'success'}
                >
                  {subRecord?.status === 'cancelled' ? 'Cancelled' : expiringWarning ? `Expires in ${daysLeft}d` : 'Active'}
                </Chip>
              </Box>
              <LinearProgress
                determinate
                value={subUsedPct}
                color={expiringWarning ? 'warning' : 'primary'}
                sx={{ mb: 1, borderRadius: 'sm' }}
              />
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {subRecord?.status === 'cancelled'
                  ? `Access until ${subEndDate}`
                  : `Renews ${subEndDate}`}
              </Typography>
            </Box>
          )}

          {/* Expiring-soon warning banner */}
          {expiringWarning && subEndDate && (
            <Alert
              color="warning"
              variant="soft"
              sx={{ mb: 2, borderRadius: 'md' }}
              endDecorator={
                goldPlan && plan === 'silver' ? (
                  <Button
                    size="sm"
                    color="warning"
                    variant="solid"
                    onClick={() => handleUpgrade(goldPlan)}
                    loading={paying === goldPlan.id}
                    disabled={!!paying}
                    sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
                  >
                    Renew
                  </Button>
                ) : silverPlan && plan === 'silver' ? (
                  <Button size="sm" color="warning" variant="solid"
                    onClick={() => handleUpgrade(silverPlan)}
                    loading={paying === silverPlan.id} disabled={!!paying}
                    sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
                  >Renew</Button>
                ) : goldPlan ? (
                  <Button size="sm" color="warning" variant="solid"
                    onClick={() => handleUpgrade(goldPlan)}
                    loading={paying === goldPlan.id} disabled={!!paying}
                    sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
                  >Renew</Button>
                ) : null
              }
            >
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                  ⚠️ Renewing in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                </Typography>
                <Typography level="body-xs">
                  Renew now to avoid any interruption to your insights.
                </Typography>
              </Box>
            </Alert>
          )}

          {/* Paid plan features list — hardcoded per plan to match landing page */}
          {isPaidPlan && !isExpired && (
            <Box sx={{ mb: 2.5 }}>
              <Typography level="title-sm" sx={{ mb: 1 }}>Included in your plan</Typography>
              <Stack spacing={0.75}>
                {(plan === 'gold'
                  ? [
                      'All of Silver +',
                      'Self & Spouse + 4 family members',
                      'Marriage Compatibility Analysis',
                      'Combined Family Score Card',
                      'Personal Muhurta Advisor',
                      'Life Event Finder',
                      '6 general AI chat questions/month',
                      '6 life area analyses/month',
                      'Proactive Daily/Weekly Forecast Alerts',
                      'Yogas & predictions',
                      'Divisional charts',
                      'Future Event Predictor',
                      'Horoscope linked Vastu',
                    ]
                  : [
                      'Self & Spouse + 2 family members (4 charts)',
                      'Family members chart analysis',
                      'Life Areas Dashboard & Remedies',
                      'Proactive Daily/Weekly Forecast Alerts',
                      '3 general AI chat questions/month',
                      '3 life area analyses/month',
                      'Yogas & predictions',
                      'Divisional charts',
                      'Future Event Predictor',
                      'Horoscope linked Vastu',
                    ]
                ).map((f: string, i: number) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <CheckCircleIcon sx={{ fontSize: 15, color: plan === 'gold' ? '#DAA520' : '#A0A8B0', mt: 0.15, flexShrink: 0 }} />
                    <Typography level="body-xs" sx={{ fontWeight: f === 'All of Silver +' ? 700 : 400 }}>{f}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Renew / Upgrade section — paid plans only */}
          {isPaidPlan && !isExpired && !isAdmin && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography level="title-sm" sx={{ mb: 1.5, fontWeight: 700 }}>
                {plan === 'gold' ? '🔄 Renew Your Plan' : '🔄 Renew or Upgrade'}
              </Typography>

              {/* Silver: two cards side-by-side — Renew Silver + Upgrade to Gold */}
              {plan === 'silver' && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 1.5 }}>
                  {/* Renew Silver */}
                  {silverPlan && (
                    <Card variant="outlined" sx={{
                      flex: 1, p: 2, display: 'flex', flexDirection: 'column',
                      borderColor: 'rgba(192,192,192,0.4)',
                      transition: 'all 0.25s ease',
                      '&:hover': { boxShadow: '0 8px 24px rgba(192,192,192,0.2)', transform: 'translateY(-2px)' },
                    }}>
                      <Typography level="title-sm" sx={{ color: '#A8A8A8', fontWeight: 700, mb: 0.25 }}>Silver</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1, flex: 1 }}>
                        Renew current plan · +1 year
                      </Typography>
                      <Typography level="title-sm" sx={{ fontWeight: 800, mb: 1.5 }}>
                        ₹{silverPlan.price_inr.toLocaleString()}<Typography level="body-xs" component="span" sx={{ ml: 0.4, color: 'text.secondary' }}>/yr</Typography>
                      </Typography>
                      <Button
                        fullWidth size="sm" variant="outlined"
                        loading={paying === silverPlan.id} disabled={!!paying}
                        onClick={() => handleUpgrade(silverPlan)}
                        sx={{
                          borderColor: '#C0C0C0', color: '#707070', fontWeight: 700,
                          '&:hover': { background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)', color: '#fff', borderColor: 'transparent' },
                        }}
                      >
                        Renew Silver
                      </Button>
                    </Card>
                  )}

                  {/* Upgrade to Gold */}
                  {goldPlan && (
                    <Card variant="outlined" sx={{
                      flex: 1, p: 2, display: 'flex', flexDirection: 'column',
                      position: 'relative',
                      borderColor: 'rgba(218,165,32,0.55)', borderWidth: 2,
                      transition: 'all 0.25s ease',
                      '&:hover': { boxShadow: '0 10px 30px rgba(218,165,32,0.22)', transform: 'translateY(-2px)' },
                    }}>
                      <Chip size="sm" variant="solid" sx={{
                        position: 'absolute', top: -10, right: 12,
                        background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                        color: '#fff', fontWeight: 700, fontSize: '10px',
                      }}>⭐ Recommended</Chip>
                      <Typography level="title-sm" sx={{ color: '#DAA520', fontWeight: 700, mb: 0.25 }}>Gold</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1, flex: 1 }}>
                        Marriage Compatibility, Life Event Finder, 6 AI chats/mo
                      </Typography>
                      <Typography level="title-sm" sx={{ fontWeight: 800, mb: 1.5 }}>
                        ₹{goldPlan.price_inr.toLocaleString()}<Typography level="body-xs" component="span" sx={{ ml: 0.4, color: 'text.secondary' }}>/yr</Typography>
                      </Typography>
                      <Button
                        fullWidth size="sm"
                        loading={paying === goldPlan.id} disabled={!!paying}
                        onClick={() => handleUpgrade(goldPlan)}
                        sx={{
                          background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                          color: '#fff', border: 'none', fontWeight: 700,
                          '&:hover': { background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)', boxShadow: '0 4px 16px rgba(184,134,11,0.3)' },
                        }}
                      >
                        Upgrade to Gold
                      </Button>
                    </Card>
                  )}
                </Box>
              )}

              {/* Gold: renew in advance */}
              {plan === 'gold' && goldPlan && (
                <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                  <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
                    Renew in advance — your current access continues uninterrupted and the new year begins after your current term ends.
                  </Typography>
                  <Button
                    fullWidth size="sm"
                    loading={paying === goldPlan.id} disabled={!!paying}
                    onClick={() => handleUpgrade(goldPlan)}
                    sx={{
                      background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                      color: '#fff', border: 'none', fontWeight: 700,
                      '&:hover': { background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)', boxShadow: '0 4px 16px rgba(184,134,11,0.3)' },
                    }}
                  >
                    Renew Gold · ₹{goldPlan.price_inr.toLocaleString()}/yr
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* Upgrade plan cards for trial-active and expired users */}
          {showUpgradeCards && !isAdmin && (
            <>
              {!isExpired && inTrial && (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1.5 }}>
                  Upgrade now to keep access after your trial ends.
                </Typography>
              )}
              {isExpired && (
                <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1.5 }}>
                  Your charts, predictions, and AI insights are waiting. Pick a plan to resume instantly.
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: 'stretch' }}>
                {/* Silver */}
                {silverPlan && (
                  <Card variant="outlined" sx={{
                    flex: 1, p: 2, display: 'flex', flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    borderColor: 'rgba(192,192,192,0.35)',
                    '&:hover': { boxShadow: '0 12px 36px rgba(192,192,192,0.18)', transform: 'translateY(-3px)', borderColor: '#C0C0C0' },
                  }}>
                    <Typography level="title-md" sx={{ mb: 0.25, color: '#A8A8A8', fontWeight: 700 }}>Silver</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>₹62/month · Billed yearly</Typography>
                    <Typography level="h4" sx={{ mb: 1, fontWeight: 800 }}>
                      ₹{silverPlan.price_inr.toLocaleString()}
                      <Typography level="body-xs" component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>/yr</Typography>
                    </Typography>
                    <Stack spacing={0.5} sx={{ flex: 1, mb: 1.5 }}>
                      {[
                        'Self & Spouse + 2 family members (4 charts)',
                        'Family members chart analysis',
                        'Life Areas Dashboard & Remedies',
                        'Proactive Daily/Weekly Forecast Alerts',
                        '3 AI chat questions/month',
                        '3 life area analyses/month',
                        'Yogas & predictions',
                        'Divisional charts',
                        'Future Event Predictor',
                        'Horoscope linked Vastu',
                      ].map(f => (
                        <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 13, color: '#A8A8A8', flexShrink: 0 }} />
                          <Typography level="body-xs">{f}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Button
                      fullWidth size="sm" variant="outlined"
                      loading={paying === silverPlan.id} disabled={!!paying}
                      onClick={() => handleUpgrade(silverPlan)}
                      sx={{
                        borderColor: '#C0C0C0', color: '#808080', fontWeight: 700,
                        '&:hover': { background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)', color: '#fff', borderColor: 'transparent' },
                      }}
                    >
                      Choose Silver
                    </Button>
                  </Card>
                )}

                {/* Gold */}
                {goldPlan && (
                  <Card variant="outlined" sx={{
                    flex: 1, p: 2, display: 'flex', flexDirection: 'column',
                    position: 'relative', transition: 'all 0.3s ease',
                    borderColor: 'rgba(218,165,32,0.5)', borderWidth: 2,
                    '&:hover': { boxShadow: '0 16px 48px rgba(218,165,32,0.25)', transform: 'translateY(-3px)' },
                  }}>
                    <Chip variant="solid" size="sm" sx={{
                      position: 'absolute', top: -10, right: 14,
                      background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                      color: '#fff', fontWeight: 700, fontSize: '10px',
                    }}>⭐ POPULAR</Chip>
                    <Typography level="title-md" sx={{ mb: 0.25, color: '#DAA520', fontWeight: 700 }}>Gold</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>₹97/month · Billed yearly</Typography>
                    <Typography level="h4" sx={{ mb: 1, fontWeight: 800 }}>
                      ₹{goldPlan.price_inr.toLocaleString()}
                      <Typography level="body-xs" component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>/yr</Typography>
                    </Typography>
                    <Stack spacing={0.5} sx={{ flex: 1, mb: 1.5 }}>
                      {[
                        'All of Silver +',
                        'Self & Spouse + 4 family members',
                        'Marriage Compatibility Analysis',
                        'Combined Family Score Card',
                        'Personal Muhurta Advisor',
                        'Life Event Finder',
                        '6 AI chat questions/month',
                        '6 life area analyses/month',
                      ].map(f => (
                        <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 13, color: '#DAA520', flexShrink: 0 }} />
                          <Typography level="body-xs" sx={{ fontWeight: f === 'All of Silver +' ? 700 : 400 }}>{f}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Button
                      fullWidth size="sm"
                      loading={paying === goldPlan.id} disabled={!!paying}
                      onClick={() => handleUpgrade(goldPlan)}
                      sx={{
                        background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                        color: '#fff', border: 'none', fontWeight: 700,
                        '&:hover': { background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)', boxShadow: '0 6px 20px rgba(184,134,11,0.35)' },
                      }}
                    >
                      Choose Gold
                    </Button>
                  </Card>
                )}
              </Box>
            </>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
}
